/**
 * ConsignmentPage
 *
 * Consignment management hub.
 * Three tabs:
 *   1. הזמנות קונסגנציה — ConsignmentOrdersAdminTab (B2B orders with order_type=consignment)
 *   2. החזרות RMA קונסגנציה — ConsignmentRMAAdminTab
 *   3. דיווחי מכירות — ConsignmentSaleReportsAdminTab (Phase 91)
 *
 * Route: /app/inventory/consignment
 */

import { useState } from 'react';
import { Boxes, RotateCcw, ClipboardCheck } from 'lucide-react';
import { useCompany } from '@/context/CompanyContext';
import { ConsignmentOrdersAdminTab }       from './ConsignmentOrdersAdminTab';
import { ConsignmentRMAAdminTab }          from './ConsignmentRMAAdminTab';
import { ConsignmentSaleReportsAdminTab }  from './ConsignmentSaleReportsAdminTab';

type Tab = 'orders' | 'rma' | 'sale_reports';

export function ConsignmentPage() {
  const { activeCompanyId } = useCompany();
  const [activeTab,         setActiveTab]         = useState<Tab>('orders');
  const [pendingOrders,     setPendingOrders]     = useState(0);
  const [pendingReports,    setPendingReports]    = useState(0);

  if (!activeCompanyId) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
        יש לבחור חברה פעילה
      </div>
    );
  }

  const subtitleMap: Record<Tab, string> = {
    orders:       'הזמנות B2B קונסגנציה נכנסות',
    rma:          'החזרות RMA קונסגנציה',
    sale_reports: 'דיווחי מכירות שהוגשו על ידי לקוחות / סוכנים',
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">

      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-teal-100 rounded-xl">
          <Boxes className="w-6 h-6 text-teal-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ניהול קונסגנציה</h1>
          <p className="text-sm text-gray-500">{subtitleMap[activeTab]}</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1.5 bg-gray-100/80 rounded-2xl p-1.5 w-fit flex-wrap">

        {/* Orders tab */}
        <button
          onClick={() => setActiveTab('orders')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-xl
                      transition-all duration-150 whitespace-nowrap
                      ${activeTab === 'orders'
                        ? 'bg-white text-teal-700 shadow-sm font-semibold'
                        : 'text-gray-500 hover:text-gray-700'
                      }`}
        >
          <Boxes className="w-4 h-4" />
          הזמנות קונסגנציה
          {pendingOrders > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold
                             bg-amber-100 text-amber-700 border border-amber-200 leading-none">
              {pendingOrders}
            </span>
          )}
        </button>

        {/* RMA tab */}
        <button
          onClick={() => setActiveTab('rma')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-xl
                      transition-all duration-150 whitespace-nowrap
                      ${activeTab === 'rma'
                        ? 'bg-white text-teal-700 shadow-sm font-semibold'
                        : 'text-gray-500 hover:text-gray-700'
                      }`}
        >
          <RotateCcw className="w-4 h-4" />
          החזרות RMA
        </button>

        {/* Sale reports tab */}
        <button
          onClick={() => setActiveTab('sale_reports')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-xl
                      transition-all duration-150 whitespace-nowrap
                      ${activeTab === 'sale_reports'
                        ? 'bg-white text-teal-700 shadow-sm font-semibold'
                        : 'text-gray-500 hover:text-gray-700'
                      }`}
        >
          <ClipboardCheck className="w-4 h-4" />
          דיווחי מכירות
          {pendingReports > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold
                             bg-amber-100 text-amber-700 border border-amber-200 leading-none">
              {pendingReports}
            </span>
          )}
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'orders' && (
        <ConsignmentOrdersAdminTab
          companyId={activeCompanyId}
          onPendingCount={setPendingOrders}
        />
      )}

      {activeTab === 'rma' && (
        <ConsignmentRMAAdminTab companyId={activeCompanyId} />
      )}

      {activeTab === 'sale_reports' && (
        <ConsignmentSaleReportsAdminTab
          companyId={activeCompanyId}
          onPendingCount={setPendingReports}
        />
      )}
    </div>
  );
}

export default ConsignmentPage;
